import type { Progress } from "@/domain/progress/model/progress";
import { hasGrandStamp, TOTAL_LEVELS } from "@/domain/progress/model/progress";
import type { MonName } from "@/presentation/components/icons/icon-names";
import { LABELS, levelDisplayName } from "@/presentation/i18n/labels";
import { computeStampTilt } from "./stamp-tilt";

/** 免状帳の枠1つ分の表示モデル（screen-flow.md §8.3・ui-design.md §5.10・§6.1）。 */
export interface StampFrame {
  /** 枠の一意なID。段の枠は`levelId`と同じ、免許皆伝の枠のみ`"grand"`。 */
  readonly id: string;
  /** 遷移先のlevelId。免許皆伝の枠は遷移先を持たないため`null`（N-006は段の枠だけが対象）。 */
  readonly levelId: string | null;
  readonly earned: boolean;
  readonly iconName: MonName;
  readonly label: string;
  /** CSSの`rotate()`に渡す角度（度）。段のIDから決定的に算出する（獲得済みの枠にのみ適用する）。 */
  readonly tiltDegrees: number;
}

/**
 * `Progress`から8枠（level-1〜level-7 + 免許皆伝）の表示モデルを組み立てる。
 * 並び順がそのままレイアウト順（4×2グリッド。1行目level-1〜4、2行目level-5〜7+免許皆伝）になる。
 *
 * 【日付表示の省略について】ui-design.md §5.10 は獲得済み枠に「授かった日」の表示を求めているが、
 * `Progress`（src/domain/progress/model/progress.ts）は`stamps: ReadonlySet<string>`という
 * 集合のみを持ち、個々のスタンプの獲得日時をドメインモデルが保持していないため、今回のスコープでは省略する。
 */
export function buildStampFrames(progress: Progress): readonly StampFrame[] {
  const levelFrames: StampFrame[] = [];
  for (let number = 1; number <= TOTAL_LEVELS; number += 1) {
    const levelId = `level-${number}`;
    const earned = progress.stamps.has(levelId);
    levelFrames.push({
      id: levelId,
      levelId,
      earned,
      iconName: `mon-${number}` as MonName,
      label: levelDisplayName(levelId) ?? levelId,
      tiltDegrees: earned ? computeStampTilt(levelId) : 0,
    });
  }

  const grandEarned = hasGrandStamp(progress);
  const grandFrame: StampFrame = {
    id: "grand",
    levelId: null,
    earned: grandEarned,
    iconName: "mon-master",
    label: LABELS.grandStamp,
    tiltDegrees: grandEarned ? computeStampTilt("grand") : 0,
  };

  return [...levelFrames, grandFrame];
}

/**
 * 初期フォーカス先の枠index（screen-flow.md §8.3「最初の未獲得枠」）。
 * 全枠が獲得済み（全制覇）なら最後の枠（免許皆伝）を返す。
 */
export function findInitialFocusIndex(frames: readonly StampFrame[]): number {
  const firstUnearned = frames.findIndex((frame) => !frame.earned);
  return firstUnearned === -1 ? frames.length - 1 : firstUnearned;
}
